import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, CalendarDays } from "lucide-react";
import { useAuth } from "../lib/auth";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_elite-services-29/artifacts/3lxe01bx_IMG_2710.jpeg";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/membership", label: "Membership" },
  { to: "/gift-vouchers", label: "Gift" },
  { to: "/book", label: "Booking" },
  { to: "/partners", label: "Partners" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();

  const onDarkHero = loc.pathname === "/" && !scrolled;

  useEffect(() => setOpen(false), [loc.pathname]);
  useEffect(() => {
    const onS = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onS);
    return () => window.removeEventListener("scroll", onS);
  }, []);

  const barBg = scrolled
    ? "bg-[#F5EFE4]/95 backdrop-blur-xl border-b border-black/5"
    : loc.pathname === "/" ? "bg-transparent" : "bg-[#F5EFE4]";

  const textCls = onDarkHero ? "text-[#F5EFE4]" : "text-[#1A1815]";
  const logoFilter = onDarkHero ? { filter: "invert(1) brightness(1.2)" } : {};

  return (
    <header data-testid="site-header" className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${barBg}`}>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-6">
        <Link to="/" data-testid="brand-link" className="flex items-center shrink-0">
          <img src={LOGO_URL} alt="Luxor Services & Concierge" className="h-16 w-auto object-contain" style={onDarkHero ? { filter: "invert(1) brightness(0.85) contrast(1.4)", mixBlendMode: "screen" } : { mixBlendMode: "darken", filter: "brightness(1.08) contrast(1.05)" }} />
        </Link>
        <nav className="hidden xl:flex items-center gap-8">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) => `text-[11px] uppercase tracking-[0.24em] transition-colors ${isActive ? "text-[#C5A059] border-b border-[#C5A059] pb-1" : `${textCls} hover:text-[#C5A059]`}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden xl:flex items-center gap-4 shrink-0">
          {user ? (
            <>
              <Link to={user.role === "admin" ? "/admin" : "/dashboard"} data-testid="header-dashboard-link" className={`text-[11px] uppercase tracking-[0.24em] ${textCls} hover:text-[#C5A059]`}>
                {user.role === "admin" ? "Admin" : "Dashboard"}
              </Link>
              <button data-testid="header-logout-btn" onClick={async () => { await logout(); nav("/"); }} className={`text-[11px] uppercase tracking-[0.24em] ${textCls} hover:text-[#C5A059]`}>
                Sign out
              </button>
            </>
          ) : (
            <Link to="/login" data-testid="header-login-link" className={`text-[11px] uppercase tracking-[0.24em] ${textCls} hover:text-[#C5A059]`}>Sign In</Link>
          )}
          <Link to="/book" className="luxor-btn-primary" data-testid="header-cta-book">
            <CalendarDays size={14} /> Enquire Now
          </Link>
        </div>
        <button data-testid="mobile-menu-toggle" onClick={() => setOpen((v) => !v)} className={`xl:hidden p-2 ${textCls}`} aria-label="Menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="xl:hidden bg-[#1A1815] border-t border-white/5">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`mobile-nav-${n.label.toLowerCase()}`} className="text-sm uppercase tracking-[0.22em] text-[#F5EFE4]">
                {n.label}
              </Link>
            ))}
            <div className="h-px bg-white/10 my-2" />
            {user ? (
              <>
                <Link to={user.role === "admin" ? "/admin" : "/dashboard"} className="text-sm uppercase tracking-[0.22em] text-[#C5A059]">
                  {user.role === "admin" ? "Admin" : "Dashboard"}
                </Link>
                <button onClick={async () => { await logout(); nav("/"); }} className="text-left text-sm uppercase tracking-[0.22em] text-[#F5EFE4]/70">Sign out</button>
              </>
            ) : (
              <Link to="/login" className="text-sm uppercase tracking-[0.22em] text-[#F5EFE4]/80">Sign In</Link>
            )}
            <Link to="/book" className="luxor-btn-primary justify-center mt-2">
              <CalendarDays size={14} /> Enquire Now
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
